import { useEffect, useRef, useState, useCallback } from 'react';

const WS_RECONNECT_DELAY = 3000; // 3 seconds
const WS_HEARTBEAT_INTERVAL = 30000; // 30 seconds

/**
 * Custom hook for WebSocket connection to Support chat
 * Handles connection, reconnection, and message routing
 */
export function useSupportWebSocket(onMessage) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const subscribedTicketsRef = useRef(new Set());
  const onMessageRef = useRef(onMessage);

  // Keep onMessage ref updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const getWebSocketUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    // No token in URL — browser sends HttpOnly cookie automatically (same-origin)
    return `${wsProtocol}://${wsHost}/api/ws/support`;
  }, []);

  const connect = useCallback(() => {
    const url = getWebSocketUrl();
    if (!url) {
      console.log('[WS] Could not build WebSocket URL, skipping');
      return;
    }

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      console.log('[WS] Connecting to:', url.split('?')[0]);
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Connected successfully');
        setIsConnected(true);
        setConnectionError(null);

        // Start heartbeat
        heartbeatRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ action: 'ping' }));
          }
        }, WS_HEARTBEAT_INTERVAL);

        // Re-subscribe to any previously subscribed tickets
        subscribedTicketsRef.current.forEach(ticketId => {
          ws.send(JSON.stringify({ action: 'subscribe_ticket', ticket_id: ticketId }));
        });
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle pong silently
          if (data.type === 'pong') return;
          
          // Handle connection confirmation
          if (data.type === 'connected') {
            console.log('[WS] Connection confirmed:', data.message);
            return;
          }

          // Handle subscription confirmations
          if (data.type === 'subscribed') {
            console.log('[WS] Subscribed to ticket:', data.ticket_id);
            return;
          }

          // Forward other messages to handler via ref
          if (onMessageRef.current) {
            onMessageRef.current(data);
          }
        } catch (error) {
          console.error('[WS] Failed to parse message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[WS] WebSocket error:', error);
        setConnectionError('Connection error');
      };

      ws.onclose = (event) => {
        console.log('[WS] Connection closed:', event.code, event.reason);
        setIsConnected(false);
        
        // Clear heartbeat
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }

        // Attempt to reconnect unless closed intentionally (code 1000)
        if (event.code !== 1000 && event.code !== 4001) {
          console.log('[WS] Scheduling reconnect...');
          reconnectTimeoutRef.current = setTimeout(connect, WS_RECONNECT_DELAY);
        }
      };
    } catch (error) {
      console.error('[WS] Failed to create WebSocket:', error);
      setConnectionError('Failed to connect');
    }
  }, [getWebSocketUrl]); // Removed onMessage from deps - using ref instead

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Intentional disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    subscribedTicketsRef.current.clear();
  }, []);

  const subscribeToTicket = useCallback((ticketId) => {
    subscribedTicketsRef.current.add(ticketId);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'subscribe_ticket',
        ticket_id: ticketId
      }));
    }
  }, []);

  const unsubscribeFromTicket = useCallback((ticketId) => {
    subscribedTicketsRef.current.delete(ticketId);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'unsubscribe_ticket',
        ticket_id: ticketId
      }));
    }
  }, []);

  const sendTypingIndicator = useCallback((ticketId, isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'typing',
        ticket_id: ticketId,
        is_typing: isTyping
      }));
    }
  }, []);

  // Connect on mount
  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Reconnect when token changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          connect();
        } else {
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect]);

  return {
    isConnected,
    connectionError,
    subscribeToTicket,
    unsubscribeFromTicket,
    sendTypingIndicator,
    reconnect: connect
  };
}

export default useSupportWebSocket;
